import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/useToast';
import { useI18n } from '@/hooks/useI18n';

// ─── Response types ────────────────────────────────────────────────────────────

export interface FunctionStats {
  slug: string;
  total_calls: number;
  success_calls: number;
  error_calls: number;
  avg_execution_ms: number | null;
  daily: Array<{ date: string; calls: number }>;
}

export interface AdminStatsResponse {
  edge_functions: {
    functions: FunctionStats[];
    period_days: number;
  };
  usage: {
    db_size_bytes: number | null;
    db_size_limit_bytes: number | null;
    storage_size_bytes: number | null;
    storage_size_limit_bytes: number | null;
    bandwidth_bytes: number | null;
    bandwidth_limit_bytes: number | null;
    monthly_active_users: number | null;
    mau_limit: number | null;
    func_invocations_month: number | null;
    func_invocations_limit: number | null;
  };
  db: {
    size_bytes: number;
    total_users: number;
    mau_30d: number;
  };
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

interface UseAdminStatsOptions {
  /** Number of past days to include (default: 7). */
  days?: number;
}

interface UseAdminStatsResult {
  data: AdminStatsResponse | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export function useAdminStats(
  options: UseAdminStatsOptions = {}
): UseAdminStatsResult {
  const { days = 7 } = options;
  const [data, setData] = useState<AdminStatsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { t } = useI18n();

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const { data: result, error: invokeError } = await supabase.functions.invoke('admin-stats', {
        body: { days },
      });

      if (invokeError) {
        const msg = String(t('errors.loadFailed'));
        setError(msg);
        toast({
          title: String(t('common.error')),
          description: msg,
          variant: 'destructive',
        });
        return;
      }

      setData(result as AdminStatsResponse);
    } catch {
      const msg = String(t('errors.generic'));
      setError(msg);
      toast({
        title: String(t('common.error')),
        description: msg,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [days, t]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run only on mount

  return { data, loading, error, refresh: fetchData };
}

export default useAdminStats;
